import sdk from "@repo/api-sdk";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { regionsQueryKeys } from "./queryKeys";

/**
 * Represents a country option for use in forms
 */
export interface CountryOption {
  /** ISO 2-letter country code (e.g., "AU", "US") */
  code: string;
  /** Display name of the country (e.g., "Australia") */
  name: string;
}

/**
 * Query options for fetching regions
 */
export function regionsQuery() {
  return queryOptions({
    queryKey: regionsQueryKeys.lists(),
    queryFn: async () => {
      const data = await sdk.store.region.list();
      return data;
    },
    staleTime: 30 * 60 * 1000, // 30 minutes - regions rarely change
    gcTime: 60 * 60 * 1000 // 1 hour
  });
}

/**
 * Hook for fetching regions
 */
export function useRegions(options?: { enabled?: boolean }) {
  return useQuery({
    ...regionsQuery(),
    ...options
  });
}

/**
 * Hook for fetching a unique list of countries from all regions
 *
 * Returns a deduplicated list of countries sorted alphabetically by name.
 * Countries are collected from all regions and merged into a single list.
 *
 * @example
 * ```tsx
 * const { countries, isLoading, error } = useCountries();
 *
 * return (
 *   <select>
 *     {countries.map((country) => (
 *       <option key={country.code} value={country.code}>
 *         {country.name}
 *       </option>
 *     ))}
 *   </select>
 * );
 * ```
 */
export function useCountries(options?: { enabled?: boolean }) {
  const { data, isLoading, error, ...rest } = useRegions(options);

  const countries = useMemo(() => {
    if (!data?.regions) {
      return [];
    }

    // Collect all countries from all regions
    const countryMap = new Map<string, CountryOption>();

    for (const region of data.regions) {
      if (region.countries) {
        for (const country of region.countries) {
          // Use iso_2 as the unique key to avoid duplicates
          if (country.iso_2 && !countryMap.has(country.iso_2)) {
            countryMap.set(country.iso_2, {
              code: country.iso_2.toUpperCase(),
              name:
                country.display_name ||
                country.name ||
                country.iso_2.toUpperCase()
            });
          }
        }
      }
    }

    // Convert to array and sort alphabetically by name
    return Array.from(countryMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [data?.regions]);

  return {
    countries,
    regions: data?.regions,
    isLoading,
    error,
    ...rest
  };
}

/**
 * The store's display currency (ISO, e.g. `"aud"`), from the first configured
 * region. Used to format prices that don't come with their own currency (e.g.
 * the stock-reorder diff, which resolves offers independent of any order).
 * `undefined` until regions load; `convertToLocale` falls back sensibly.
 */
export function useStoreCurrencyCode(): string | undefined {
  const { data } = useRegions();
  return data?.regions?.[0]?.currency_code;
}

/**
 * Represents a country option for use in forms
 */
export interface ProvinceOption {
  code: string;
  /** Display name */
  name: string;
}

interface CountryProvinceObject {
  type: "Region" | "State";
  options: Record<string, string>;
}

const countryProvinceMap: Record<string, CountryProvinceObject> = {
  NZ: {
    type: "Region", // keep it Pascal case
    options: {
      AUK: "Auckland",
      BOP: "Bay of Plenty",
      CAN: "Canterbury",
      CIT: "Chatham Islands Territory",
      GIS: "Gisborne",
      WGN: "Greater Wellington",
      HKB: "Hawke's Bay",
      MWT: "Manawatū-Whanganui",
      MBH: "Marlborough",
      NSN: "Nelson",
      NTL: "Northland",
      OTA: "Otago",
      STL: "Southland",
      TKI: "Taranaki",
      TAS: "Tasman",
      WKO: "Waikato",
      WTC: "West Coast"
    }
  },
  AU: {
    type: "State", // keep it Pascal case
    options: {
      ACT: "Australian Capital Territory",
      NSW: "New South Wales",
      NT: "Northern Territory",
      QLD: "Queensland",
      SA: "South Australia",
      TAS: "Tasmania",
      VIC: "Victoria",
      WA: "Western Australia"
    }
  }
};

export function useProvinces(country_iso_2: string = "au") {
  const provinces = useMemo(() => {
    const record = countryProvinceMap[country_iso_2.toLocaleUpperCase()];
    if (record) {
      return {
        type: record.type,
        options: Object.entries(record.options).map(([code, name]) => {
          return {
            code,
            name
          } satisfies ProvinceOption;
        })
      };
    }

    return undefined;
  }, [country_iso_2]);

  return {
    provinces,
    isLoading: false,
    error: null
  };
}
